import { ethers } from "ethers";
import EthereumProvider from "@walletconnect/ethereum-provider";

export const SUPPORTED_WALLETS = {
  METAMASK: "metamask",
  COINBASE: "coinbase",
  WALLETCONNECT: "walletconnect",
  INJECTED: "injected", // Any injected wallet
};

let walletConnectProvider = null;

export const isMobileBrowser = () =>
  /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || "");

export const buildMetaMaskDeepLink = (targetUrl = window.location.href) => {
  const normalizedTarget = String(targetUrl).replace(/^https?:\/\//i, "");
  return `https://metamask.app.link/dapp/${normalizedTarget}`;
};

export const openMetaMaskDeepLink = (targetUrl = window.location.href) => {
  if (!isMobileBrowser()) {
    return false;
  }

  window.location.href = buildMetaMaskDeepLink(targetUrl);
  return true;
};

const getInjectedProviders = () => {
  if (!window.ethereum) {
    return [];
  }

  if (Array.isArray(window.ethereum.providers)) {
    return window.ethereum.providers;
  }

  return [window.ethereum];
};

export const getInjectedProvider = (
  walletType = SUPPORTED_WALLETS.INJECTED,
) => {
  const providers = getInjectedProviders();

  if (providers.length === 0) {
    return null;
  }

  if (walletType === SUPPORTED_WALLETS.METAMASK) {
    return providers.find((provider) => provider?.isMetaMask) || null;
  }

  if (walletType === SUPPORTED_WALLETS.COINBASE) {
    return providers.find((provider) => provider?.isCoinbaseWallet) || null;
  }

  return window.ethereum || providers[0] || null;
};

export const isWalletAvailable = (walletType) => {
  if (walletType === SUPPORTED_WALLETS.WALLETCONNECT) {
    return true;
  }

  return Boolean(getInjectedProvider(walletType));
};

export async function connectWallet(walletType = SUPPORTED_WALLETS.INJECTED) {
  try {
    let provider;

    switch (walletType) {
      case SUPPORTED_WALLETS.METAMASK:
        provider = getInjectedProvider(SUPPORTED_WALLETS.METAMASK);
        if (!provider) {
          throw new Error("MetaMask not installed");
        }
        break;

      case SUPPORTED_WALLETS.COINBASE:
        provider = getInjectedProvider(SUPPORTED_WALLETS.COINBASE);
        if (!provider) {
          throw new Error("Coinbase Wallet not installed");
        }
        break;

      case SUPPORTED_WALLETS.INJECTED:
        // Use any injected provider (MetaMask, Coinbase, Base, etc.)
        provider = getInjectedProvider(SUPPORTED_WALLETS.INJECTED);
        if (!provider) {
          throw new Error(
            "No Web3 wallet detected. Please install MetaMask, Coinbase Wallet, or another Web3 wallet.",
          );
        }
        break;

      case SUPPORTED_WALLETS.WALLETCONNECT:
        // Initialize WalletConnect provider
        walletConnectProvider = await EthereumProvider.init({
          projectId: "fd75db2f71a3b5a1c949161488479537", // Replace with your WalletConnect Project ID
          chains: [137], // Polygon Mainnet
          showQrModal: true,
          qrModalOptions: {
            themeMode: "dark",
          },
        });

        // Enable session (shows QR code modal)
        const wcAccounts = await walletConnectProvider.enable();
        provider = walletConnectProvider;

        // Set up event listeners for disconnect
        walletConnectProvider.on("disconnect", () => {
          console.log("WalletConnect disconnected");
          walletConnectProvider = null;
        });

        return {
          address: wcAccounts[0].toLowerCase(),
          provider: new ethers.providers.Web3Provider(walletConnectProvider),
        };

      default:
        throw new Error("Unsupported wallet type");
    }

    // Request account access
    const accounts = await provider.request({
      method: "eth_requestAccounts",
    });

    // Check network - using Polygon mainnet
    const chainId = await provider.request({ method: "eth_chainId" });
    const polygonChainId = "0x89"; // 137 in hex - Polygon Mainnet

    if (chainId !== polygonChainId) {
      // Attempt to switch to Polygon mainnet
      try {
        await provider.request({
          method: "wallet_switchEthereumChain",
          params: [{ chainId: polygonChainId }],
        });
      } catch (switchError) {
        // Chain not added, add it
        if (switchError.code === 4902) {
          await provider.request({
            method: "wallet_addEthereumChain",
            params: [
              {
                chainId: polygonChainId,
                chainName: "Polygon",
                nativeCurrency: {
                  name: "POL",
                  symbol: "POL",
                  decimals: 18,
                },
                rpcUrls: ["https://polygon-rpc.com/"],
                blockExplorerUrls: ["https://polygonscan.com/"],
              },
            ],
          });
        } else {
          throw switchError;
        }
      }
    }

    return {
      address: accounts[0].toLowerCase(),
      provider: new ethers.providers.Web3Provider(provider),
    };
  } catch (error) {
    console.error("Wallet connection error:", error);
    throw error;
  }
}

export function detectWalletType() {
  const metamaskProvider = getInjectedProvider(SUPPORTED_WALLETS.METAMASK);
  if (metamaskProvider) return SUPPORTED_WALLETS.METAMASK;

  const coinbaseProvider = getInjectedProvider(SUPPORTED_WALLETS.COINBASE);
  if (coinbaseProvider) return SUPPORTED_WALLETS.COINBASE;

  const injectedProvider = getInjectedProvider(SUPPORTED_WALLETS.INJECTED);
  if (!injectedProvider) return null;

  if (injectedProvider.isBase) return "Base Wallet";

  return SUPPORTED_WALLETS.INJECTED;
}

export async function disconnectWalletConnect() {
  if (walletConnectProvider) {
    await walletConnectProvider.disconnect();
    walletConnectProvider = null;
  }
}
